import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, shortHash } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Card } from '../components/ui';
import { Field } from '../components/Field';
import { IconVersions } from '../components/Icons';

export function Versions() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const archives = useAsync(() => api.listArchives(200), []);
  const [archiveId, setArchiveId] = useState(params.get('archive') ?? '');
  const [objectId, setObjectId] = useState(params.get('object') ?? '');

  return (
    <>
      <PageHeader title={t('versions.title')} subtitle={t('versions.subtitle')} />
      <ApiState {...archives} onRetry={archives.reload}>
        {(page) => {
          const options = page.items.map((a) => ({ id: a.id, name: a.name }));
          const effectiveArchive = options.some((o) => o.id === archiveId) ? archiveId : options[0]?.id ?? '';
          return (
            <div className="stack">
              <Card>
                <div className="card__body">
                  {options.length === 0 ? (
                    <EmptyState title={t('archives.empty')} />
                  ) : (
                    <div className="form-row">
                      <Field label={t('versions.pickArchive')}>
                        {(p) => (
                          <select
                            className="select"
                            {...p}
                            value={effectiveArchive}
                            onChange={(e) => {
                              setArchiveId(e.target.value);
                              setObjectId('');
                            }}
                          >
                            {options.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <ObjectPicker
                        archiveId={effectiveArchive}
                        value={objectId}
                        onChange={setObjectId}
                      />
                    </div>
                  )}
                </div>
              </Card>

              {effectiveArchive && objectId ? (
                <VersionList archiveId={effectiveArchive} objectId={objectId} />
              ) : (
                <Card>
                  <EmptyState title={t('versions.prompt')} icon={<IconVersions width={26} height={26} />} />
                </Card>
              )}
            </div>
          );
        }}
      </ApiState>
    </>
  );
}

function ObjectPicker({
  archiveId,
  value,
  onChange,
}: {
  archiveId: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const objects = useAsync(() => api.listObjects(archiveId, 200), [archiveId]);

  // Reset selection if it no longer belongs to the loaded archive.
  useEffect(() => {
    if (objects.data && !objects.data.items.some((o) => o.id === value)) {
      onChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects.data]);

  return (
    <Field label={t('versions.pickObject')}>
      {(p) => (
        <select
          className="select"
          {...p}
          value={value}
          disabled={objects.status !== 'success' || (objects.data?.items.length ?? 0) === 0}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">
            {objects.status === 'loading'
              ? t('common.loading')
              : (objects.data?.items.length ?? 0) === 0
                ? t('versions.noObjects')
                : `— ${t('versions.pickObject')} —`}
          </option>
          {objects.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.path}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

function VersionList({ archiveId, objectId }: { archiveId: string; objectId: string }) {
  const { t } = useTranslation();
  const lang = useLang();
  const versions = useAsync(() => api.listVersions(archiveId, objectId), [archiveId, objectId]);

  return (
    <Card>
      <ApiState {...versions} onRetry={versions.reload}>
        {(page) =>
          page.items.length === 0 ? (
            <EmptyState title={t('versions.empty')} icon={<IconVersions width={26} height={26} />} />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">{t('versions.colVersion')}</th>
                    <th className="num">{t('common.size')}</th>
                    <th>{t('archives.colHash')}</th>
                    <th>{t('common.createdAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((v) => (
                    <tr key={v.version}>
                      <td className="num cell-strong">v{v.version}</td>
                      <td className="num">{formatBytes(v.bytes, lang)}</td>
                      <td>
                        <span className="mono" title={v.sha256}>
                          {shortHash(v.sha256)}
                        </span>
                      </td>
                      <td className="muted">{formatDateTime(v.createdAt, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </ApiState>
    </Card>
  );
}
